import {createAsyncThunk, createSlice, SerializedError} from "@reduxjs/toolkit";
import {RootState} from "app/realtime-store/redux-store";
import type Guid from "common/values/guid/guid";
import {WritableDraft} from "immer";
import EntityMemberAPIService from "legal-entities/entities/entity-member/api/entity-member-api-service";
import EntityMember from "legal-entities/entities/entity-member/entity-member";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import {useSelector} from "react-redux";
import Session from "users/session/session";

type EntityMemberStoreState = {
  byEntityId: {
    entries: Record<string, EntityMember[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  }
};

const initialState: EntityMemberStoreState = {
  byEntityId: {
    entries: {},
    loading: {},
    error: {}
  }
};
export const populateEntityMembers = createAsyncThunk<
  EntityMember[],
  { session: Session; entity: LegalEntity }
>(
  "entityMembers/getEntityMembersByForum",
  async ({ session, entity }: { session: Session, entity: LegalEntity }, thunkAPI) => {
    const apiService = new EntityMemberAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getLegalEntityMembersUserInfo(entity, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const entityMembersSlice = createSlice({
  name: "entityMembers",
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder.addCase(populateEntityMembers.pending, (state, action) => {
      const entityId = action.meta.arg.entity.id.value;
      if (!entityId) {
        return;
      }
      state.byEntityId.loading[entityId] = true;
      state.byEntityId.error[entityId] = null;
    });
    builder.addCase(populateEntityMembers.fulfilled, (state, action) => {
      const entityId = action.meta.arg.entity.id.value;
      if (!entityId) {
        return;
      }

      state.byEntityId.loading[entityId] = false;

      let existingMembers: WritableDraft<EntityMember>[] = [];

      for (const member of action.payload) {
        if (existingMembers.some((existingMember) => existingMember.id?.isEqualTo(member.id))) {
          existingMembers = existingMembers.filter((existingMessage) => !existingMessage.id?.isEqualTo(member.id));
        }
        existingMembers.push(member);
        state.byEntityId.entries[entityId] = existingMembers;
      }
    });

    builder.addCase(populateEntityMembers.rejected, (state, action) => {
      const entityId = action.meta.arg.entity.id.value;
      if (!entityId) {
        return;
      }
      state.byEntityId.loading[entityId] = false;
      state.byEntityId.error[entityId] = action.payload as SerializedError;
    });
  }
});

export const getEntityMembers = (entityId: Guid) =>
  useSelector(
    (state: RootState) =>
      state.entityMembers?.byEntityId.entries[entityId.value]
  );
